// Top-bar pieces shared by the site console and the platform console: the global search button
// the notification bell and the account menu (profile, sign-in and security, sign out).
import { useCallback, useEffect, useRef, useState } from "react";
import { Avatar, Button, Icon, Menu, Popover, useToast, type IconName, type MenuItem } from "@vavr/ui";
import { alertCategory, alertPrefs, api, askDesktop, chime, desktopState, navigate, showDesktop, type DesktopState } from "../shared/index.ts";
import type { Me } from "./data.tsx";

export const isMac = () => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

/** Opens global search; Ctrl/⌘ K does the same from anywhere. */
export function SearchButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="pmu-c-search" onClick={onClick} aria-label="Search" aria-keyshortcuts="Control+K Meta+K">
      <Icon name="search" />
      <span className="pmu-c-search__text">Search…</span>
      <kbd>{isMac() ? "⌘" : "Ctrl"} K</kbd>
    </button>
  );
}

/** The account button, top right: who is signed in, their profile, security, `items`, sign out. */
export function AccountMenu({ me, role, items = [], profile = true }: { me: Me; role: string; items?: MenuItem[]; profile?: boolean }) {
  const signOut = async () => { await api("POST", "/api/v1/auth/logout").catch(() => undefined); navigate("/login", true); };
  return (
    <Menu
      trigger={
        <button type="button" className="pmu-c-account" aria-label={`Account: ${me.account.name}`}>
          <Avatar name={me.account.name} kind="adult" size="sm" />
          <span className="pmu-c-account__text"><span className="pmu-c-account__name">{me.account.name}</span>{role && <span className="pmu-c-account__role">{role}</span>}</span>
          <Icon name="chevron-down" className="pmu-c-account__chevron" />
        </button>
      }
      header={
        <div className="pmu-c-account-head">
          <Avatar name={me.account.name} kind="adult" size="md" />
          <div><div className="pmu-c-account-head__name">{me.account.name}</div><div className="pmu-c-account-head__email">{me.account.email}</div></div>
        </div>
      }
      groups={[
        [
          ...(profile ? [{ id: "profile", label: "Your profile", icon: "user" as const, onSelect: () => navigate("/console/profile") }] : []),
          { id: "security", label: "Sign-in and security", icon: "lock", onSelect: () => navigate("/account/security") },
          ...items,
        ],
        [{ id: "logout", label: "Sign out", icon: "logout", onSelect: () => void signOut() }],
      ]}
    />
  );
}

export interface Notice { id: string; site_id: string | null; kind: string; title: string; body: string; href: string | null; count: number; created_at: number; read_at: number | null }

const NOTICE_ICON = (kind: string): IconName =>
  kind === "message" || kind.startsWith("ticket") || kind === "support" ? "message" : kind.startsWith("bus") ? "bus" : kind.startsWith("lost") ? "search"
    : kind === "announcement" ? "info" : kind === "breakglass" ? "lock" : kind === "onboarding_done" ? "check-circle"
    : kind === "message_flagged" || kind.startsWith("safety") ? "shield" : kind === "exception" ? "warning" : kind.startsWith("closing") ? "clock" : "bell";

function ago(ts: number, now = Date.now()) {
  const m = Math.round((now - ts) / 60_000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "Yesterday" : d < 7 ? `${d} days ago` : new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const POLL_MS = 15_000; // visible tab
const HIDDEN_POLL_MS = 60_000; // background tab: still checks, so desktop alerts arrive

/**
 * The bell: what Vavr told this person (every module's notifications for their roles), newest
 * first, with the unread count. Checked every 30 s while the page is visible and on return to it.
 * `open` follows a notification (the console switches to its site first).
 */
/**
 * `base`: where the list lives (Vavr Ops passes /api/ops/notifications); `empty`: what an empty bell
 * says; `scope`: whose alert settings (per browser); `settingsHref`: a link to fuller settings.
 * Anything new pops up on the page, and as a desktop notification with a chime when allowed.
 */
export function NotificationBell({ open: follow = (n) => { if (n.href) navigate(n.href); }, base = "/api/v1/me/notifications", empty = "Messages, bus changes, reports and alerts for your roles show up here.", scope = "console", settingsHref }: { open?: (n: Notice) => void; base?: string; empty?: string; scope?: string; settingsHref?: string }) {
  const toast = useToast();
  const seen = useRef<Set<string> | null>(null);
  const followRef = useRef(follow);
  followRef.current = follow;
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const [desktop, setDesktop] = useState<DesktopState>(() => (typeof window === "undefined" ? "unsupported" : desktopState()));
  const [data, setData] = useState<{ unread: number; items: Notice[] } | null>(null);
  const [shown, setShown] = useState(false);
  const alert = useCallback((incoming: Notice[]) => {
    let fresh = incoming;
    const prefs = alertPrefs(scope);
    const hidden = document.visibilityState !== "visible";
    // Quiet kinds stay in the bell only (the console's alert settings choose them per computer).
    fresh = fresh.filter((n) => !(prefs.quiet ?? []).includes(alertCategory(n.kind)));
    if (!fresh.length) return;
    for (const n of fresh.slice(0, 3)) {
      toastRef.current.show({ title: n.title, tone: n.kind === "ticket_late" || n.kind === "ticket_urgent" || n.kind === "breakglass" || n.kind.startsWith("safety") ? "warning" : "info", timeout: n.kind === "ticket_urgent" ? 0 : 10_000, priority: "high",
        description: <span className="pmu-stack pmu-c-tight"><span>{n.body}</span>{n.href && <Button size="sm" variant="secondary" onClick={() => followRef.current(n)}>Open</Button>}</span> });
      if (prefs.desktop && (hidden || !document.hasFocus())) showDesktop(n.title, n.body, () => followRef.current(n), n.id);
    }
    if (fresh.length > 3) toastRef.current.show({ title: `${fresh.length - 3} more new notifications`, tone: "info" });
    if (prefs.sound && fresh.length) chime();
  }, [scope]);
  const load = useCallback(() => api<{ unread: number; items: Notice[] }>("GET", `${base}?limit=30`).then((d) => {
    // The first load only learns what is already there; later loads alert for anything new and unread.
    const fresh = seen.current ? d.items.filter((n) => !n.read_at && !seen.current!.has(n.id)) : [];
    seen.current = new Set(d.items.map((n) => n.id));
    setData(d);
    if (fresh.length) alert(fresh);
  }, () => undefined), [base, alert]);
  useEffect(() => {
    void load();
    let t: ReturnType<typeof setTimeout>;
    const schedule = () => { t = setTimeout(() => { void load(); schedule(); }, document.visibilityState === "visible" ? POLL_MS : HIDDEN_POLL_MS); };
    schedule();
    const back = () => { if (document.visibilityState === "visible") void load(); };
    document.addEventListener("visibilitychange", back);
    return () => { clearTimeout(t); document.removeEventListener("visibilitychange", back); };
  }, [load]);
  // The tab's title carries the count, so a background tab shows there is something new.
  const count = data?.unread ?? 0;
  useEffect(() => {
    if (typeof document === "undefined") return;
    const plain = document.title.replace(/^\(\d+\+?\) /, "");
    document.title = count ? `(${count > 99 ? "99+" : count}) ${plain}` : plain;
  });
  const markRead = async (body: { ids: string[] } | { all: true }) => {
    setData((d) => d && { unread: "all" in body ? 0 : Math.max(0, d.unread - body.ids.length), items: d.items.map((n) => ("all" in body || body.ids.includes(n.id) ? { ...n, read_at: n.read_at ?? Date.now() } : n)) });
    await api("POST", `${base}/read`, body).catch(() => undefined);
  };
  const unread = data?.unread ?? 0;
  const items = data?.items ?? [];
  return (
    <Popover
      open={shown} onOpenChange={(o) => { setShown(o); if (o) void load(); }}
      title="Notifications"
      actions={unread > 0 ? <Button size="sm" variant="ghost" onClick={() => void markRead({ all: true })}>Mark all read</Button> : undefined}
      trigger={
        <button type="button" className="pmu-c-bell" aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}>
          <Icon name="bell" />
          {unread > 0 && <span className="pmu-c-bell__count" aria-hidden="true">{unread > 99 ? "99+" : unread}</span>}
        </button>
      }
    >
      {desktop === "default" && (
        <div className="pmu-c-notices__prompt">
          <span className="pmu-small">Get a desktop alert when something new comes in, even in another tab.</span>
          <Button size="sm" variant="secondary" onClick={async () => setDesktop(await askDesktop())}>Turn on desktop alerts</Button>
        </div>
      )}
      {items.length === 0 ? (
        <div className="pmu-c-notices__empty"><Icon name="bell" /><p>You're all caught up.</p><p className="pmu-muted">{empty}</p>{settingsHref && <p><a className="pmu-link" href={settingsHref} onClick={(e) => { e.preventDefault(); setShown(false); navigate(settingsHref); }}>Notification settings</a></p>}</div>
      ) : (
        <ul className="pmu-c-notices">
          {items.map((n) => (
            <li key={n.id}>
              <button type="button" className={`pmu-c-notice${n.read_at ? "" : " pmu-c-notice--unread"}`}
                onClick={() => { if (!n.read_at) void markRead({ ids: [n.id] }); setShown(false); follow(n); }}>
                <span className="pmu-c-notice__icon" aria-hidden="true"><Icon name={NOTICE_ICON(n.kind)} /></span>
                <span className="pmu-c-notice__text">
                  <span className="pmu-c-notice__title">{n.title}{n.count > 1 && <span className="pmu-c-notice__count"> · {n.count} new</span>}</span>
                  <span className="pmu-c-notice__body">{n.body}</span>
                  <span className="pmu-c-notice__when">{ago(n.created_at)}</span>
                </span>
                {!n.read_at && <span className="pmu-c-notice__dot" aria-label="Unread" />}
              </button>
            </li>
          ))}
        </ul>
      )}
      {settingsHref && items.length > 0 && <p className="pmu-c-notices__foot"><a className="pmu-link pmu-small" href={settingsHref} onClick={(e) => { e.preventDefault(); setShown(false); navigate(settingsHref); }}>Notification settings</a></p>}
    </Popover>
  );
}
